/** Machine-readable GIF alt prefix embedded in message body (no schema field yet). */
const GIF_ALT_MARKER = /^<!--ascend-gif-alt:(.+?)-->\n?/

const DEFAULT_GIF_ALT = 'Animated GIF'
const DEFAULT_IMAGE_ALT = 'Image attachment'

/** Persist Giphy alt text in body without showing it as a visible caption. */
export function encodeGifMessageBody(altText?: string, caption?: string): string {
  const alt = altText?.trim()
  const cap = caption?.trim()
  if (alt) {
    const marker = `<!--ascend-gif-alt:${JSON.stringify(alt)}-->`
    return cap ? `${marker}\n${cap}` : marker
  }
  return cap ?? ''
}

export function parseGifMessageBody(body: string): {
  altText: string | null
  visibleText: string
} {
  const trimmed = body.trim()
  if (!trimmed) return { altText: null, visibleText: '' }

  const match = trimmed.match(GIF_ALT_MARKER)
  if (!match) return { altText: null, visibleText: trimmed }

  try {
    const altText = JSON.parse(match[1]) as unknown
    const visibleText = trimmed.slice(match[0].length).trim()
    return {
      altText: typeof altText === 'string' && altText.trim() ? altText.trim() : null,
      visibleText,
    }
  } catch {
    return { altText: null, visibleText: trimmed }
  }
}

export function resolveMediaMessageDisplay(args: {
  text: string
  attachmentType?: string | null
  attachmentUrl?: string | null
}): { attachmentAlt: string; visibleText: string | null } {
  const trimmed = args.text.trim()
  const hasAttachment = Boolean(args.attachmentUrl)

  if (args.attachmentType === 'GIF' && hasAttachment) {
    const { altText, visibleText } = parseGifMessageBody(trimmed)
    if (altText) {
      return {
        attachmentAlt: altText,
        visibleText: visibleText || null,
      }
    }
    // Legacy GIF sends stored Giphy alt text as plain body.
    if (trimmed) {
      return { attachmentAlt: trimmed, visibleText: null }
    }
    return { attachmentAlt: DEFAULT_GIF_ALT, visibleText: null }
  }

  if (args.attachmentType === 'IMAGE' && hasAttachment) {
    // The caption is rendered ONCE as visible text below the image — do NOT
    // reuse it as the <img> alt, or a failed image load paints the browser's
    // broken-image chrome + alt text and the message reads as duplicated.
    return {
      attachmentAlt: DEFAULT_IMAGE_ALT,
      visibleText: trimmed || null,
    }
  }

  return {
    attachmentAlt: 'Message attachment',
    visibleText: trimmed || null,
  }
}

/** Thread-list preview for GIF alt metadata (client-side). */
export function formatGifAltThreadPreview(body: string): string | null {
  const trimmed = body.trim()
  if (!trimmed) return null

  const { altText, visibleText } = parseGifMessageBody(trimmed)
  if (altText) {
    return visibleText ? truncatePreview(visibleText) : 'GIF'
  }

  return null
}

function truncatePreview(text: string, max = 120): string {
  const trimmed = text.trim()
  if (trimmed.length <= max) return trimmed
  return `${trimmed.slice(0, max - 1)}…`
}
